import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CreditCard, Globe, Loader2, Mail, MapPin, MessageCircle, Phone, ShieldCheck, Stethoscope } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ACADEMY } from "@/constants/testIds";

// Fix J (2026-10-27): la web publica de la academia (/). Textos del Notion «Catálogo de Cursos 2026»;
// cursos, precios, módulos, fechas y contacto salen del sistema (GET /api/public/academy/catalog).
const INTRO = "Catálogo oficial 2026 con formación presencial, práctica clínica supervisada y certificaciones con validez internacional.";
const PILLARS = [
  { icon: Stethoscope, title: "Práctica real supervisada", text: "Capacitación clínica directa sobre modelos reales para afianzar cada técnica." },
  { icon: Globe, title: "Acreditación global", text: "Diplomas certificados para respaldar tu negocio a nivel internacional." },
  { icon: CreditCard, title: "Planes de pago", text: "Financiamiento con Cherry, Zelle, tarjeta y efectivo." },
];
const EMPTY_FORM = { name: "", phone: "", email: "", course_id: "", message: "", website: "" };
const usd = (n) => `$${new Intl.NumberFormat("en-US").format(Number(n) || 0)}`;
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("es-US", { day: "numeric", month: "short", year: "numeric" });

const CSS = `
html { scroll-behavior: smooth; }
@keyframes aame-fill { from { transform: scaleY(0); } to { transform: scaleY(1); } }
.aame-fill { transform-box: fill-box; transform-origin: bottom; animation: aame-fill 1.6s cubic-bezier(.22,.8,.3,1) .2s both; }
@media (prefers-reduced-motion: reduce) { html { scroll-behavior: auto; } .aame-fill { animation: none; } }
`;

function groupCatalog(courses) {
  const modules = [];
  const byTitle = new Map();
  courses.forEach((c) => {
    const title = c.module || "Otros cursos";
    if (!byTitle.has(title)) {
      const m = { title, groups: new Map() };
      byTitle.set(title, m);
      modules.push(m);
    }
    const m = byTitle.get(title);
    const g = c.group || "";
    if (!m.groups.has(g)) m.groups.set(g, []);
    m.groups.get(g).push(c);
  });
  return modules.map((m) => ({ title: m.title, groups: [...m.groups.entries()].map(([name, items]) => ({ name, items })) }));
}

// El indice del catalogo es una jeringa: cada graduacion lleva a un modulo.
function SyringeIndex({ modules, onPick }) {
  const n = modules.length;
  const ys = n === 1 ? [224] : modules.map((_, i) => 100 + (i * 240) / (n - 1));
  const ticks = [];
  for (let y = 86, i = 0; y <= 376; y += 14, i += 1) ticks.push({ y, long: i % 5 === 0 });
  return (
    <div className="relative hidden md:block h-[440px] w-[480px] max-w-full">
      <svg viewBox="0 0 214 440" width="214" height="440" className="absolute left-0 top-0" aria-hidden="true">
        <rect x="40" y="0" width="60" height="9" rx="4.5" fill="#fff" fillOpacity=".2" stroke="#fff" strokeOpacity=".55" />
        <rect x="64" y="10" width="12" height="54" fill="#fff" fillOpacity=".12" stroke="#fff" strokeOpacity=".45" />
        <rect x="20" y="58" width="100" height="8" rx="4" fill="#fff" fillOpacity=".2" stroke="#fff" strokeOpacity=".55" />
        <rect x="40" y="64" width="60" height="320" rx="10" fill="#fff" fillOpacity=".05" stroke="#fff" strokeOpacity=".6" strokeWidth="1.5" />
        <rect className="aame-fill" x="42" y="86" width="56" height="296" rx="8" fill="#40C3C1" fillOpacity=".92" />
        <rect x="42" y="66" width="56" height="20" rx="3" fill="#fff" fillOpacity=".3" />
        {ticks.map((t) => <line key={t.y} x1={98 - (t.long ? 20 : 10)} x2="98" y1={t.y} y2={t.y} stroke="#fff" strokeOpacity=".55" />)}
        <path d="M54 384 H86 L80 402 H60 Z" fill="#fff" fillOpacity=".18" stroke="#fff" strokeOpacity=".55" />
        <line x1="70" y1="402" x2="70" y2="434" stroke="#fff" strokeOpacity=".75" strokeWidth="1.5" />
        {ys.map((y, i) => (
          <g key={i}>
            <line x1="100" x2="208" y1={y} y2={y} stroke="#fff" strokeOpacity=".35" />
            <circle cx="100" cy={y} r="4" fill="#F3DCD2" />
          </g>
        ))}
      </svg>
      <nav aria-label="Módulos del catálogo">
        {modules.map((m, i) => (
          <button
            key={m.title}
            type="button"
            onClick={() => onPick(i)}
            style={{ top: ys[i] }}
            className="absolute left-[222px] w-[256px] -translate-y-1/2 text-left font-serif text-base leading-snug text-white/85 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#40C3C1] rounded-sm"
          >
            <span className="mr-2 text-[#40C3C1]">{i + 1}</span>{m.title}
          </button>
        ))}
      </nav>
    </div>
  );
}

function CourseRow({ course, onInfo }) {
  return (
    <li className="grid gap-x-8 gap-y-3 py-5 sm:grid-cols-[1fr_auto] sm:items-center group" data-testid={`${ACADEMY.webCourseRowPrefix}${course.id}`}>
      <div>
        <h4 className="font-serif text-[1.075rem] leading-snug text-foreground group-hover:text-brand transition-colors">{course.name}</h4>
        {course.description && <p className="mt-1 text-sm text-muted-foreground max-w-xl">{course.description}</p>}
        {course.practices_required > 0 && <p className="mt-1 text-sm text-muted-foreground">{course.practices_required} prácticas supervisadas</p>}
        {course.dates.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">Consulta las próximas fechas.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm text-foreground/80">
            {course.dates.map((d) => (
              <li key={d.id}>
                <span className="font-medium">{fmtDate(d.start_date)}{d.end_date && d.end_date !== d.start_date ? ` al ${fmtDate(d.end_date)}` : ""}</span>
                {d.start_time && <span> · {d.start_time}{d.end_time ? `–${d.end_time}` : ""}</span>}
                {d.location && <span> · {d.location}</span>}
                {d.price !== course.price && <span> · {usd(d.price)} ({d.price_label})</span>}
                {d.seats_left != null && <span className={d.seats_left === 0 ? "text-[#A34B49]" : "text-brand"}> · {d.seats_left === 0 ? "Sin lugares" : d.seats_left === 1 ? "Queda 1 lugar" : `Quedan ${d.seats_left} lugares`}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex items-center justify-between gap-5 sm:justify-end">
        <p className="font-serif text-2xl font-bold text-brand whitespace-nowrap">{usd(course.price)} <span className="text-xs font-semibold text-muted-foreground">USD</span></p>
        <button
          type="button"
          onClick={() => onInfo(course.id)}
          className="h-10 px-4 rounded-md border border-brand text-brand text-sm font-medium hover:bg-brand hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
          data-testid={`${ACADEMY.webInfoButtonPrefix}${course.id}`}
        >
          Pedir información
        </button>
      </div>
    </li>
  );
}

function InterestForm({ modules, form, setForm, nameRef }) {
  const [state, setState] = useState({ status: "idle", error: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setState({ status: "sending", error: "" });
    try {
      await api.post("/public/academy/interest", {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null,
        course_id: form.course_id || null, message: form.message.trim() || null, website: form.website,
      });
      setState({ status: "done", error: "" });
      setForm(EMPTY_FORM);
    } catch (err) {
      const msg = err.response ? formatApiErrorDetail(err.response.data?.detail) : "No pudimos enviar tu consulta. Revisa tu conexión e intenta de nuevo.";
      setState({ status: "error", error: msg });
    }
  };
  if (state.status === "done") {
    return (
      <div className="rounded-2xl bg-white border border-border p-8" role="status" data-testid={ACADEMY.webSuccess}>
        <h3 className="font-serif text-2xl text-foreground">Recibimos tu consulta</h3>
        <p className="mt-2 text-muted-foreground">Te vamos a contactar al teléfono o email que dejaste.</p>
        <button type="button" onClick={() => setState({ status: "idle", error: "" })} className="mt-5 text-sm font-medium text-brand underline underline-offset-4">Enviar otra consulta</button>
      </div>
    );
  }
  const field = "h-11 bg-white";
  return (
    <form onSubmit={submit} className="rounded-2xl bg-white border border-border p-6 sm:p-8 space-y-4" data-testid={ACADEMY.webForm}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="web-name" className="mb-1.5 block">Nombre completo</Label>
          <Input id="web-name" ref={nameRef} required minLength={2} maxLength={120} autoComplete="name" className={field} value={form.name} onChange={set("name")} data-testid={ACADEMY.webNameInput} />
        </div>
        <div>
          <Label htmlFor="web-phone" className="mb-1.5 block">Teléfono o WhatsApp</Label>
          <Input id="web-phone" type="tel" required minLength={6} maxLength={40} autoComplete="tel" className={field} value={form.phone} onChange={set("phone")} data-testid={ACADEMY.webPhoneInput} />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="web-email" className="mb-1.5 block">Email (opcional)</Label>
          <Input id="web-email" type="email" autoComplete="email" className={field} value={form.email} onChange={set("email")} data-testid={ACADEMY.webEmailInput} />
        </div>
        <div>
          <Label htmlFor="web-course" className="mb-1.5 block">Curso de interés</Label>
          <select id="web-course" className="h-11 w-full rounded-md border border-input bg-white px-3 text-base md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" value={form.course_id} onChange={set("course_id")} data-testid={ACADEMY.webCourseSelect}>
            <option value="">Todavía no sé</option>
            {modules.map((m) => (
              <optgroup key={m.title} label={m.title}>
                {m.groups.flatMap((g) => g.items).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </optgroup>
            ))}
          </select>
        </div>
      </div>
      <div>
        <Label htmlFor="web-message" className="mb-1.5 block">Mensaje (opcional)</Label>
        <Textarea id="web-message" rows={3} maxLength={1000} className="bg-white" value={form.message} onChange={set("message")} data-testid={ACADEMY.webMessageInput} />
      </div>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 opacity-0" value={form.website} onChange={set("website")} name="website" />
      {state.status === "error" && <p className="text-sm text-white bg-[#A34B49] rounded-md px-3 py-2" role="alert" data-testid={ACADEMY.webError}>{state.error}</p>}
      <button type="submit" disabled={state.status === "sending"} className="h-12 w-full sm:w-auto px-8 rounded-md bg-brand hover:bg-brand-hover text-white font-medium transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2" data-testid={ACADEMY.webSubmit}>
        {state.status === "sending" ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</> : "Enviar consulta"}
      </button>
    </form>
  );
}

export default function LandingPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const nameRef = useRef(null);

  const load = useCallback(() => {
    setError(false);
    api.get("/public/academy/catalog").then(({ data: d }) => setData(d)).catch(() => setError(true));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const prev = document.title;
    document.title = "AAME Academy | Catálogo de Cursos 2026";
    return () => { document.title = prev; };
  }, []);

  const courses = useMemo(() => data?.courses || [], [data]);
  const modules = useMemo(() => groupCatalog(courses), [courses]);
  const academy = data?.academy || {};
  const wa = (academy.whatsapp || "").replace(/\D/g, "");

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pickModule = (i) => { setActive(null); setTimeout(() => goTo(`modulo-${i + 1}`), 60); };
  const askInfo = (courseId) => {
    setForm((f) => ({ ...f, course_id: courseId }));
    goTo("contacto");
    setTimeout(() => nameRef.current?.focus({ preventScroll: true }), 600);
  };
  const shown = active === null ? modules.map((m, i) => ({ m, i })) : [{ m: modules[active], i: active }];
  const link = "text-sm text-white/80 hover:text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#40C3C1] rounded-sm";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid={ACADEMY.webPage}>
      <style>{CSS}</style>

      <section className="relative overflow-hidden bg-[#04383D] text-white" style={{ backgroundImage: "radial-gradient(52% 70% at 78% 46%, rgba(64,195,193,.16), transparent 70%)" }}>
        <header className="mx-auto flex max-w-6xl items-center justify-between px-5 sm:px-8 pt-6">
          <a href="/" className="flex items-baseline gap-2" aria-label="AAME Academy">
            <span className="font-serif text-2xl font-bold tracking-[0.28em]">AAME</span>
            <span className="text-sm text-white/70">Academy</span>
          </a>
          <nav className="flex items-center gap-5 sm:gap-7" aria-label="Principal">
            <button type="button" onClick={() => goTo("cursos")} className={`${link} hidden sm:block`}>Cursos</button>
            <button type="button" onClick={() => goTo("contacto")} className={`${link} hidden sm:block`}>Contacto</button>
            <a href="/academia" className={`${link} border border-white/30 rounded-md px-3 py-1.5`} data-testid={ACADEMY.webLoginLink}>Ingresar</a>
          </nav>
        </header>

        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 sm:px-8 pb-16 pt-14 sm:pt-20 lg:grid-cols-[1fr_480px] lg:pb-24">
          <div>
            <h1 className="font-serif text-[2.4rem] sm:text-5xl lg:text-[3rem] xl:text-[3.5rem] font-bold leading-[1.06] tracking-tight max-w-[15ch] sm:max-w-[17ch]">Formación en estética médica con práctica real supervisada</h1>
            <p className="mt-6 max-w-[34rem] text-lg leading-relaxed text-white/80">{INTRO}</p>
            <p className="mt-3 inline-flex items-center gap-2 text-[#F3DCD2]"><ShieldCheck className="h-5 w-5" /> No se requiere SSN</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button type="button" onClick={() => goTo("cursos")} className="h-12 rounded-md bg-white px-7 font-medium text-[#04383D] hover:bg-[#DDF1F1] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#40C3C1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#04383D]">Ver los cursos</button>
              <button type="button" onClick={() => goTo("contacto")} className="h-12 rounded-md border border-white/40 px-7 font-medium text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#40C3C1]">Quiero información</button>
            </div>
            {modules.length > 0 && (
              <ul className="mt-10 space-y-2 md:hidden">
                {modules.map((m, i) => (
                  <li key={m.title}><button type="button" onClick={() => pickModule(i)} className="text-left font-serif text-[15px] text-white/85"><span className="mr-2 text-[#40C3C1]">{i + 1}</span>{m.title}</button></li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-center lg:justify-end">
            {modules.length > 0 && <SyringeIndex modules={modules} onPick={pickModule} />}
          </div>
        </div>
      </section>

      <section className="bg-white" aria-label="Por qué AAME">
        <div className="mx-auto grid max-w-6xl md:grid-cols-3 md:divide-x divide-border px-5 sm:px-8">
          {PILLARS.map((p) => (
            <div key={p.title} className="py-8 md:px-8 md:first:pl-0 md:last:pr-0">
              <p.icon className="h-6 w-6 text-brand" />
              <h2 className="mt-4 font-serif text-lg text-foreground">{p.title}</h2>
              <p className="mt-1.5 text-muted-foreground max-w-xs">{p.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="cursos" className="scroll-mt-0 py-16 sm:py-20" data-testid={ACADEMY.webCatalog}>
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Catálogo de cursos 2026</h2>
          {modules.length > 0 && <p className="mt-2 text-muted-foreground">{courses.length} cursos en {modules.length} módulos. Elige uno y deja tus datos para recibir información.</p>}
        </div>

        {modules.length > 1 && (
          <div className="mt-8 border-y border-border">
            <div className="mx-auto flex max-w-6xl gap-2 overflow-x-auto md:flex-wrap md:overflow-visible px-5 sm:px-8 py-3">
              {[{ title: "Todos los módulos" }, ...modules].map((m, i) => {
                const on = (i === 0 && active === null) || active === i - 1;
                return (
                  <button
                    key={m.title}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setActive(i === 0 ? null : i - 1)}
                    className={`whitespace-nowrap rounded-full border px-4 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${on ? "border-brand bg-brand text-white" : "border-border bg-white text-foreground hover:border-brand"}`}
                    data-testid={`${ACADEMY.webFilterPrefix}${i}`}
                  >
                    {m.title}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          {!data && !error && (
            <div className="mt-10 space-y-3" aria-busy="true">
              {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 animate-pulse rounded-lg bg-[#E4F0F0]" />)}
            </div>
          )}
          {error && (
            <div className="mt-10 rounded-xl border border-border bg-white p-8 text-center">
              <p className="text-foreground">No pudimos cargar el catálogo.</p>
              <button type="button" onClick={load} className="mt-3 text-sm font-medium text-brand underline underline-offset-4">Intentar de nuevo</button>
            </div>
          )}
          {data && courses.length === 0 && <p className="mt-10 text-muted-foreground">Estamos actualizando el catálogo. Escríbenos y te contamos qué cursos hay.</p>}
          {shown.map(({ m, i }) => (
            <div key={m.title} id={`modulo-${i + 1}`} className="scroll-mt-20 pt-12">
              <div className="flex items-baseline gap-4">
                <span className="font-serif text-5xl font-bold text-brand/25" aria-hidden="true">{i + 1}</span>
                <h3 className="font-serif text-2xl sm:text-[1.7rem] leading-tight">{m.title}</h3>
              </div>
              {m.groups.map((g) => (
                <div key={g.name || "_"} className="mt-6">
                  {g.name && <p className="border-l-2 border-[#40C3C1] pl-3 font-serif text-base text-brand">{g.name}</p>}
                  <ul className="mt-2 divide-y divide-border border-y border-border">
                    {g.items.map((c) => <CourseRow key={c.id} course={c} onInfo={askInfo} />)}
                  </ul>
                </div>
              ))}
            </div>
          ))}
        </div>
      </section>

      <section id="contacto" className="scroll-mt-0 bg-[#DDF1F1] py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[.8fr_1.2fr]">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">¿Qué curso te interesa?</h2>
            <p className="mt-3 max-w-md text-lg text-foreground/80">Déjanos tus datos y te contactamos con fechas, precio y formas de pago.</p>
            <ul className="mt-8 space-y-3 text-foreground">
              {wa.length >= 8 && (
                <li>
                  <a href={`https://wa.me/${wa}?text=${encodeURIComponent("Hola, quiero información sobre los cursos de AAME Academy.")}`} target="_blank" rel="noreferrer" className="inline-flex h-11 items-center gap-2 rounded-md bg-brand px-5 font-medium text-white hover:bg-brand-hover transition-colors" data-testid={ACADEMY.webWhatsapp}>
                    <MessageCircle className="h-5 w-5" /> Escríbenos por WhatsApp
                  </a>
                </li>
              )}
              {academy.phone && <li className="flex items-center gap-3"><Phone className="h-5 w-5 text-brand" /> <a href={`tel:${academy.phone}`} className="hover:underline">{academy.phone}</a></li>}
              {academy.email && <li className="flex items-center gap-3"><Mail className="h-5 w-5 text-brand" /> <a href={`mailto:${academy.email}`} className="hover:underline">{academy.email}</a></li>}
              {academy.address && <li className="flex items-center gap-3"><MapPin className="h-5 w-5 text-brand" /> {academy.address}</li>}
            </ul>
          </div>
          <InterestForm modules={modules} form={form} setForm={setForm} nameRef={nameRef} />
        </div>
      </section>

      <footer className="bg-[#04383D] text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 sm:px-8 py-8">
          <div>
            <p className="font-serif text-lg">{academy.name || "AAME Academy"}</p>
            <p className="text-sm text-white/70">American Aesthetics Medical Education</p>
          </div>
          <p className="text-sm text-white/60">© {new Date().getFullYear()} {academy.name || "AAME Academy"}</p>
          <a href="/academia" className={link}>Ingresar al panel</a>
        </div>
      </footer>
    </div>
  );
}
